// Vue
import Vue from 'vue';
import Router from 'vue-router';

// Vuex
import store from 'store';

// Config
import config from 'config';

// Services
import { logPageView } from '../services/gtag.js';
import { tourPaths } from '../models/auth';

// Async load function that handles loading
const loadAsyncPage = (pageImport) => {
  const init = store.state.app.state.init;

  store.dispatch(`app/openLoader`);

  return new Promise(resolve => {

    pageImport
      .then(component => {
        setTimeout(() => {

          if (!init) {
            store.dispatch(`app/init`);
          }

        }, store.state.app.state.init ? 0 : config.transitionDuration);

        resolve(component);
      });
  });
};

// Base
const NotFound = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/404`));

// Top Level Pages
const Home = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/Home`));
const Terms = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/Terms`));
const InfoDentists = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/InfoDentists`));
const InfoReviewers = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/InfoReviewers`));
const UnderstandingYourReport = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/UnderstandingYourReport`));
const TrackLink = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/TrackLink`));

// Auth
const Login = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/Login`));
const Logout = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/Logout`));
const RegisterDentist = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/register/RegisterDentist`));
const RegisterPatient = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/register/RegisterPatient`));
const AuthReset = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/auth/Reset`));
const AuthConfirm = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/auth/Confirm`));

// Dashboard
const Dashboard = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/Dashboard`));
const DashboardProfileMyInfo = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/profile/MyInfo`));
const DashboardProfilePassword = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/profile/Password`));
const DashboardRequests = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/Requests`));
const DashboardReviews = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/Reviews`));
const DashboardReview = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/Review`));
const DashboardDentistLocations = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/profile/Locations`));
const DashboardReviewerApplication = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/ReviewerApplication`));
const DashboardReviewerRequests = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/ReviewerRequests`));
const DashboardReviewerRequest = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/ReviewerRequest`));
const DashboardEmailLogs = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/EmailLogs`));
const DashboardEmailActivity = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/dashboard/EmailActivity`));

// Success
const Success = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/Success`));

// Request
const Request = () => loadAsyncPage(import(/* webpackChunkName: "page-base" */ `views/Request`));

// Payment Request
const ReviewPaymentRequest = () => loadAsyncPage(import(/* webpackChunkName: "review-payment-request" */ `views/ReviewPaymentRequest`));

// Use
Vue.use(Router);

// Routes
const routes = [
  {
    path: `*`,
    name: `notFound`,
    component: NotFound,
  },
  {
    path: `/`,
    name: `home`,
    component: Home,
  },
  {
    path: `/terms-of-service`,
    name: `terms`,
    component: Terms,
  },
  {
    path: `/privacy-policy`,
    name: `privacy`,
    component: Terms,
  },
  {
    path: `/link/:payload/:redirect`,
    name: `track-link`,
    component: TrackLink,
  },

  // Auth
  {
    name: `login`,
    path: `/log-in`,
    component: Login,
    meta: {
      noAuth: true,
    },
  },
  {
    name: `logout`,
    path: `/log-out`,
    component: Logout,
    meta: {
      noFooter: true,
    },
  },
  {
    name: `register-dentist`,
    path: `/register/dentist`,
    component: RegisterDentist,
    meta: {
      noAuth: true,
    },
  },
  {
    name: `register-reviewer`,
    path: `/register/reviewer`,
    component: RegisterDentist,
    meta: {
      noAuth: true,
    },
  },
  {
    name: `register-patient`,
    path: `/register/patient`,
    component: RegisterPatient,
    meta: {
      noAuth: true,
    },
  },
  {
    name: `authReset`,
    path: `/password/forgot`,
    component: AuthReset,
    meta: {
      noAuth: true,
    },
  },
  {
    path: `/reset-password/:token`,
    redirect: to => {
      const { params } = to;
      if (params.token) {
        return { name: `authConfirm`, params: { token: params.token } };
      }
    },
  },
  {
    name: `authConfirm`,
    path: `/password/reset/:token`,
    component: AuthConfirm,
    meta: {
      noAuth: true,
    },
  },
  {
    name: `set-password`,
    path: `/set-password/:token`,
    component: AuthConfirm,

    beforeEnter: (to, from, next) => {
      const userAuthed = store.getters[`auth/isAuthenticated`];
      if (userAuthed) {
        store.dispatch(`auth/requestLogout`, {
          noRedirect: true,
        });
      }
      return next();
    },
  },

  {
    name: `understanding-your-report`,
    path: `/understanding-your-report`,
    component: UnderstandingYourReport,
  },

  // Info
  {
    name: `info-dentists`,
    path: `/dentists`,
    component: InfoDentists,
  },

  {
    name: `info-reviewers`,
    path: `/reviewers`,
    component: InfoReviewers,
  },

  // Request
  {
    name: `request`,
    path: `/request`,
    component: Request,
    meta: {
      auth: [
        1,
      ],
    },
  },
  {
    name: `request-complete`,
    path: `/request/:id`,
    component: Request,
    meta: {
      auth: [
        1,
        2,
        3,
      ],
    },
  },
  {
    name: `success`,
    path: `/success/:page`,
    component: Success,
    meta: {
      auth: true,
    },
  },
  {
    name: `review-payment-request`,
    path: `/review/payment/:id`,
    component: ReviewPaymentRequest,
    meta: {
      auth: true,
    },
  },

  {
    name: `dashboard-next-review`,
    path: `/dashboard/review`,
    component: DashboardReview,
    meta: {
      key: `dashboard`,
      auth: [
        3,
      ],
    },
  },

  // Dashboard
  {
    name: `dashboard`,
    path: `/dashboard`,
    component: Dashboard,
    meta: {
      key: `dashboard`,
    },
    children: [
      // Admin
      {
        name: `admin-email-logs`,
        path: `email-logs`,
        component: DashboardEmailLogs,
        meta: {
          key: `dashboard`,
          auth: [
            42,
          ],
        },
      },
      {
        name: `admin-email-activity`,
        path: `email-activity/:id`,
        component: DashboardEmailActivity,
        meta: {
          key: `dashboard`,
          auth: [
            42,
          ],
        },
      },
      {
        name: `admin-reviewer-requests`,
        path: `reviewer-requests`,
        component: DashboardReviewerRequests,
        meta: {
          key: `dashboard`,
          auth: [
            42,
          ],
        },
      },
      {
        name: `admin-reviewer-request`,
        path: `reviewer-request/:id`,
        component: DashboardReviewerRequest,
        meta: {
          key: `dashboard`,
          auth: [
            42,
          ],
        },
      },
      // Review
      {
        name: `dashboard-requests`,
        path: `requests`,
        component: DashboardRequests,
        meta: {
          key: `dashboard`,
          auth: true,
        },
      },

      {
        name: `dashboard-reviews`,
        path: `reviews`,
        component: DashboardReviews,
        meta: {
          key: `dashboard`,
          auth: [
            3,
          ],
        },
      },

      {
        name: `dashboard-review`,
        path: `request/:id`,
        component: DashboardReview,
        meta: {
          key: `dashboard`,
          auth: [
            1,
            2,
            3,
            42,
          ],
        },
      },

      // Profile
      {
        name: `dashboard-profile-info`,
        path: `profile`,
        component: DashboardProfileMyInfo,
        meta: {
          key: `dashboard`,
          auth: true,
        },
      },
      {
        name: `dashboard-profile-password`,
        path: `profile/password`,
        component: DashboardProfilePassword,
        meta: {
          key: `dashboard`,
          auth: true,
        },
      },

      // Dentist
      {
        name: `dashboard-dentist-locations`,
        path: `profile/locations`,
        component: DashboardDentistLocations,
        meta: {
          key: `dashboard`,
          auth: true,
        },
      },
      // Reviewer
      {
        name: `dashboard-reviewer-application`,
        path: `reviewer-application`,
        component: DashboardReviewerApplication,
        meta: {
          key: `dashboard`,
          auth: [
            2,
          ],
        },
      },
    ],
  },
];

const router = new Router({
  mode: `history`,
  routes,
});

router.beforeEach((to, from, next) => {
  const meta = to.meta;

  const auth = meta.auth || [];
  const isNoAuth = meta.noAuth === true;

  const userAuthed = store.getters[`auth/isAuthenticated`];
  const auth_token = localStorage.getItem(`auth_token`); // eslint-disable-line no-unused-vars

  // If show tour is active and user deviates away unset the tour
  const userOnTour = store.getters[`auth/showTour`];

  const role = store.getters[`auth/userRole`] || {};

  const userFollowingTour = function() {
    const tourPath = tourPaths[userOnTour] || [];
    const upcomingTourLocation = tourPath.indexOf(to.name);
    const previousTourLocation = tourPath.indexOf(from.name);

    if(to.name === `dashboard`) {
      // Ignore for now
      return true;
    }

    if(upcomingTourLocation === -1) {
      return false;
    }

    if(upcomingTourLocation === 0) {
      // Tour just started
      return true;
    }

    if(upcomingTourLocation - 1 === previousTourLocation) {
      // Is previous location immediately preceding current?
      return true;
    }

    return false; // gotta catch 'em all
  };

  if (userOnTour && store.getters[`auth/userIsRole`]([2]) && !userFollowingTour()) {
    store.dispatch(`auth/deactivateTour`);
  }

  logPageView(to.path);

  // Handle a user coming directly to a `modal-path` link
  if (to.query[`modal-path`]) {
    next({
      path: `/${to.query[`modal-path`]}`,
    });
    return false;
  }

  // Handle closing an open modal from the current route
  if (to.name === from.name && (from.query.modal || from.query[`modal-path`])) {
    store.dispatch(`modals/closeModal`);
    return false;
  }

  // Handle initial authentication
  if (!userAuthed && auth_token) {
    store.dispatch(`auth/storeRedirect`, to.path);
    store.dispatch(`auth/requestCurrentUser`, {
      auth_token,
      redirect: true,
    });
    return false;
  }

  if (to.name === `set-password` && to.query.redirect) {
    store.dispatch(`auth/storeRedirect`, to.query.redirect);
  }

  if (to.name.match(/^register-(patient|dentist|reviewer)$/) && to.query.redirect) {
    store.dispatch(`auth/storeRedirect`, to.query.redirect);
  }

  // Handle states that require auth for unauthed users
  if ((auth === true || auth.length) && !userAuthed) {
    store.dispatch(`auth/storeRedirect`, to.path);
    next({
      name: `login`,
      query: {
        redirect: to.path,
      },
    });
    return false;
  }

  // Handle pages that a user doesn't have permission to view
  // Ensure that the page that is being redirected to doesn't have any
  // specific permissions (it can be a page that requires default auth though)
  const roleObject = role.object || {};

  if ((auth.length && !store.getters[`auth/userIsRole`](auth) && userAuthed) || (to.name === `dashboard` && userAuthed)) {

    // Go to dashboard-profile-info if they just signed up
    if(!!store.getters[`auth/showTour`] && to.name === `dashboard` && store.getters[`auth/userIsRole`]([2])) {
      // Is there any user data at this point or not?
      next({
        name: `dashboard-profile-info`,
      });
      return false;
    }

    next({
      name: roleObject.defaultRoute || `dashboard-profile-info`,
    });
    return false;
  }

  // Handle states that require no auth for authed users
  if (isNoAuth && userAuthed) {
    next({
      name: roleObject.defaultRoute || `dashboard-profile-info`,
    });
    return false;
  }

  next();

  if (to.query.modal) {
    setTimeout(() => {
      store.dispatch(`modals/openModal`, to.query.modal);
    }, 500);
  }
});

export default router;



// WEBPACK FOOTER //
// ./client/router/index.js